import { ChangeDetectionStrategy, inject, Component, DestroyRef, ElementRef, effect, input, OnInit, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from '@/core/services/logger.service';
import { CommonModule } from '@angular/common';

import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { InputMask } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PessoaResponseModel } from '@/shared/components/pessoa/pesquisa/pessoa.model';
import { MedicoService } from '@/core/api/sicom-api/registro/medico/medico.service';
import { MedicoCensuraPublicaRequest, MedicoCensuraPublicaUpdateRequest, MedicoDeficienciaResponse, MedicoPeriodoResponse, MedicoRequest } from '@/core/api/sicom-api/registro/medico/medico.model';
import { DadosRegistroService } from './dados-registro.service';
import { PaisService } from '@/core/api/sicom-api/registro/medico/pais/pais.service';
import { PaisResponse } from '@/core/api/sicom-api/registro/medico/pais/pais.model';
import { DadosRegistroFormValue } from '../../medicos-form.types';
import { PessoaSaveRequest } from '@/shared/models/pessoa-save-request.model';
import { DadosRegistroMapper } from './utils/dados-registro.mapper';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';
import { AuthService } from '@/core/auth/auth.service';
import { DateUtils } from '@/shared/utils/date-utils';

import { DadosInscricaoComponent } from './sections/dados-inscricao/dados-inscricao';
import { DadosPessoaisComponent } from './sections/dados-pessoais/dados-pessoais';
import { DadosMilitaresComponent } from './sections/dados-militares/dados-militares';
import { FiliacaoComponent } from './sections/filiacao/filiacao';
import { LocalizacaoComponent } from './sections/localizacao/localizacao';
import { SuspensaoComponent } from './sections/suspensao/suspensao';
import { CensuraPublicaComponent, type CensuraPublicaItem } from './sections/censura-publica/censura-publica';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SwitchSimNao } from '@/shared/components/switch-sim-nao/switch-sim-nao';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type DeficienciaSnapshot = {
    comDeficiencia: string;
    tipoDeficiencia: string;
    origemDeficiencia: string;
    deficienciaAdaptada: string;
    outrasInformacoes: string;
};

type SuspensaoSnapshot = {
    suspensaoInicio: string;
    suspensaoFim: string;
    observacao: string;
    suspensaoDivulgaPortal: string;
    documentoSuspensaoBase64?: string;
    arquivoSuspensaoNome: string;
};

type MudancaSituacaoSnapshot = {
    dtMudancaSituacao?: string;
};

@Component({
    selector: 'app-dados-registro',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DialogoPadraoDirective,
        SwitchSimNao,
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        Dialog,
        Button,
        Select,
        DatePicker,
        InputMask,
        InputText,
        Textarea,
        PrimeTemplate,
        DadosInscricaoComponent,
        DadosPessoaisComponent,
        DadosMilitaresComponent,
        FiliacaoComponent,
        LocalizacaoComponent,
        SuspensaoComponent,
        CensuraPublicaComponent,
        CardContent,
        BotaoAcoes,
        CanDirective
    ],
    templateUrl: './dados-registro.html',
    styleUrl: './dados-registro.scss'
})
/** Aba de dados de registro: orquestra as seções do formulário da pessoa/médico. */
export class DadosRegistro implements OnInit {
    private readonly logger = inject(LoggerService);
    private readonly destroyRef = inject(DestroyRef);
    pessoa = input<Pessoa | PessoaResponseModel>();

    salvar = output<{ pessoaPartial: Partial<PessoaSaveRequest>; medicoFormValue: DadosRegistroFormValue }>();

    private readonly fb = inject(FormBuilder);
    readonly dadosRegistroForm = this.buildDadosRegistroForm();
    modoEdicao = signal(false);

    carregandoDominios = signal(true);

    opcoesSimNao = [
        { label: 'Sim', value: 'S' },
        { label: 'Não', value: 'N' }
    ];
    opcoesGenero = [
        { label: 'Masculino', value: 'M' },
        { label: 'Feminino', value: 'F' }
    ];
    opcoesNacionalidade = [
        { label: 'Brasileiro', value: 'B' },
        { label: 'Estrangeiro', value: 'E' },
        { label: 'Naturalizado', value: 'N' }
    ];
    opcoesCategoriaMilitar = [
        { label: 'Civil', value: 'C' },
        { label: 'Militar', value: 'M' }
    ];
    opcoesTipoSanguineo = [
        { label: 'A', value: 'A' },
        { label: 'B', value: 'B' },
        { label: 'AB', value: 'AB' },
        { label: 'O', value: 'O' }
    ];
    opcoesRH = [
        { label: '+', value: 'POSITIVO' },
        { label: '-', value: 'NEGATIVO' }
    ];
    opcoesDoacaoOrgaos = [
        { label: 'Doador', value: 'DOADOR' },
        { label: 'Não Doador', value: 'NAO_DOADOR' }
    ];
    opcoesTipoDeficiencia = [
        { label: 'Visual', value: 'V' },
        { label: 'Motora', value: 'M' },
        { label: 'Auditiva', value: 'A' },
        { label: 'Outras', value: 'O' }
    ];
    opcoesOrigemDeficiencia = [
        { label: 'Congênita', value: 'C' },
        { label: 'Adquirida', value: 'A' }
    ];

    opcoesSituacao = signal<Array<{ label: string; value: string }>>([]);
    opcoesTipoInscricao: Array<{ label: string; value: string }> = [];
    opcoesTipoLicenca: Array<{ label: string; value: string }> = [];
    opcoesEstadoCivil: Array<{ label: string; value: string }> = [];
    opcoesPaises: Array<{ label: string; value: number }> = [];

    suspensoes = signal<MedicoPeriodoResponse[]>([]);
    loadingSuspensoes = signal(false);
    erroSuspensoes = signal<string | undefined>(undefined);
    censurasPublicas = signal<CensuraPublicaItem[]>([]);
    loadingCensurasPublicas = signal(false);
    erroCensurasPublicas = signal<string | undefined>(undefined);
    salvandoCensuraPublica = signal(false);
    editandoCensuraPublica = signal(false);
    censuraPublicaChave = signal<string | null>(null);
    arquivoCensuraPublica: File | null = null;
    arquivoCensuraPublicaNome = '';
    tipoDeficienciaSelect = viewChild<Select>('tipoDeficienciaSelect');
    origemDeficienciaSelect = viewChild<Select>('origemDeficienciaSelect');
    deficienciaAdaptadaSelect = viewChild<Select>('deficienciaAdaptadaSelect');
    deficienciaOutras = viewChild<ElementRef<HTMLTextAreaElement>>('deficienciaOutras');
    suspensaoInicioInput = viewChild<DatePicker>('suspensaoInicioInput');
    suspensaoFimInput = viewChild<DatePicker>('suspensaoFimInput');
    observacaoSuspensaoInput = viewChild<ElementRef<HTMLInputElement>>('observacaoSuspensaoInput');
    suspensaoPdfInput = viewChild<ElementRef<HTMLInputElement>>('suspensaoPdfInput');
    censuraPdfInput = viewChild<ElementRef<HTMLInputElement>>('censuraPdfInput');

    mostrarSecaoDeficiencias = signal(false);
    mostrarAreaSuspensao = signal(false);
    paisEhBrasil = signal(true);
    formValid = signal(false);
    acoesDesabilitadas = signal(false);
    protected readonly roleDadosRegistroEditar = PERMISSOES.medicos.dadosRegistro.editar;
    /** Censura pública mora nesta aba, mas o backend a gateia por família própria (medico-routes.yml). */
    protected readonly roleCensuraPublicaEditar = PERMISSOES.medicos.censurasPublicas.editar;
    get acoesFormulario(): AppMenuItem[] {
        return [
            acaoMenuItem({
                id: 'editar',
                label: 'Editar',
                icon: 'pi pi-pencil',
                cor: 'editar',
                disabled: this.modoEdicao(),
                permission: this.roleDadosRegistroEditar,
                command: () => this.habilitarEdicao()
            })
        ];
    }

    /** Ver acoesFormulario: Salvar e Cancelar saem do menu e viram ação em destaque. */
    get itensEdicao(): AppMenuItem[] {
        const role = this.roleDadosRegistroEditar;
        return [
            acaoMenuItem({ id: 'salvar', label: 'Salvar', icon: 'pi pi-check', cor: 'salvar', disabled: !this.formValid(), permission: role, command: () => this.salvarDados() }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.cancelarEdicao() })
        ];
    }

    showDataAlteracaoDialog = false;
    mostrarDialogMudancaSituacao = false;
    mostrarDialogDeficiencia = false;
    mostrarDialogSuspensao = false;
    mostrarDialogCensuraPublica = false;
    private previousTipoInscricao: string | null = null;
    private pendingTipoInscricao: string | null = null;
    private dtAlteracaoTipoInscricao?: string;
    private dtMudancaSituacao?: string;
    dataAlteracaoTemp: string | null = null;
    dataMudancaSituacaoTemp: string | null = null;
    situacaoMudancaDescricao = '';
    arquivoSuspensao: File | null = null;
    arquivoSuspensaoNome = '';
    documentoSuspensaoBase64?: string;
    private comDeficienciaAnterior = 'N';
    private snapshotDeficiencia?: DeficienciaSnapshot;
    private previousSituacaoValue: string | null = null;
    private pendingSituacaoValue: unknown | null = null;
    private snapshotSuspensao?: SuspensaoSnapshot;
    private snapshotMudancaSituacao?: MudancaSituacaoSnapshot;

    constructor(
        private medicoService: MedicoService,
        private dadosRegistroService: DadosRegistroService,
        private paisService: PaisService,
        private mapper: DadosRegistroMapper,
        private messageService: MessageService,
        private authService: AuthService
    ) {
        effect(() => {
            const pessoaAtual = this.pessoa();
            const dominiosProntos = !this.carregandoDominios();
            this.atualizarHabilitacaoAcoes(pessoaAtual);

            if (pessoaAtual) {
                if (!this.modoEdicao()) {
                    this.preencherNomeDaPessoa(pessoaAtual);

                    if (dominiosProntos) {
                        if (this.isPessoaRequest(pessoaAtual)) {
                            this.carregarMedico(pessoaAtual);
                        } else {
                            this.carregarSituacoesIniciais();
                        }
                    }
                }
            }
        });
    }

    ngOnInit(): void {
        this.dadosRegistroForm.disable({ emitEvent: false });
        this.atualizarEstadoDisabledControlesEdicao();
        this.preencherNomeDaPessoa(this.pessoa());
        this.carregarDominios();
        this.setupFormListeners();
    }

    private buildDadosRegistroForm() {
        return this.fb.nonNullable.group({
            // maxLength espelha as colunas NM_SOLICITANTE/NM_SOCIAL (70) da tabela SOLICITANTE:
            // impede o envio de valor acima do limite (que estourava HTTP 500 no backend).
            nome: ['', [Validators.required, Validators.maxLength(70)]],
            nomeSocial: ['', Validators.maxLength(70)],
            // Não obrigatório: consulta ao banco de produção encontrou 155 médicos com
            // IN_TIPO_INSCRICAO vazio (dados legados). Exigir o campo travaria o formulário
            // desses registros (mesma armadilha corrigida no campo CPF). Não reintroduzir Validators.required aqui.
            tipoInscricao: [''],
            tipoLicenca: [''],
            emitirCertEtica: [false],
            // Não obrigatório: consulta ao banco de produção encontrou 5 médicos com
            // IN_SITUACAO vazio (dados legados). Exigir o campo travaria o formulário
            // desses registros (mesma armadilha corrigida no campo CPF). Não reintroduzir Validators.required aqui.
            situacao: [''],
            suspensaoInicio: ['', [this.dataMenorOuIgualHojeValidator()]],
            suspensaoFim: ['', [this.suspensaoFimValidator()]],
            observacao: [''],
            suspensaoDivulgaPortal: ['N'],
            censuraPublicaInicio: ['', [this.dataMenorOuIgualHojeValidator()]],
            censuraPublicaFim: [''],
            censuraPublicaObservacao: [''],
            censuraPublicaDivulgarPortal: ['N'],
            comDeficiencia: ['N'],
            tipoDeficiencia: [''],
            origemDeficiencia: [''],
            deficienciaAdaptada: [''],
            deficiencias: [''],
            categoriaMilitar: ['C'],
            nascimentoData: ['', [this.nascimentoObrigatorioValidator(), this.dataMenorOuIgualHojeValidator()]],
            nascimentoDec: ['', [this.dataMenorOuIgualHojeValidator()]],
            filiacao: [''],
            nomePai: [''],
            genero: ['M', Validators.required],
            nomeMae: [''],
            origem: [''],
            paisIdPais: this.fb.control<number | null>(null),
            nmMunicipioExterior: [''],
            nacionalidade: ['B', Validators.required],
            estadoCivil: [''],
            naturalidade: [''],
            municipioIdMunicipio: this.fb.control<number | null>(null),
            tipoSanguineo: [''],
            rh: [''],
            decMilitarData: ['', [this.dataMenorOuIgualHojeValidator()]],
            situacaoMilitar: [''],
            ministerio: [''],
            doacaoOrgaos: [''],
            tituloEleitorNumero: [''],
            tituloEleitorZona: [''],
            tituloEleitorSecao: [''],
            tituloEleitorUF: [''],
            documentoNumero: [''],
            documentoDataEmissao: ['', [this.dataMenorOuIgualHojeValidator()]],
            documentoOrgaoEmissor: [''],
            documentoUF: ['']
        });
    }

    private dataMaiorQueHojeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            if (!DateUtils.isDataValida(valor)) {
                return { dataInvalida: true };
            }

            return FormularioValidatorUtil.dataMaiorQueHoje(valor) ? null : { dataMaiorQueHojeInvalida: true };
        };
    }

    private dataMenorOuIgualHojeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            if (!DateUtils.isDataValida(valor)) {
                return { dataInvalida: true };
            }

            return FormularioValidatorUtil.dataMaiorQueHoje(valor) ? { dataMaiorQueHojeInvalida: true } : null;
        };
    }

    /**
     * Data de nascimento é obrigatória, exceto para Intercambista (`tipoInscricao === 'I'`) —
     * porte da regra de `UntCadMedico.pas:7047`, que dispensa o campo apenas nesse caso.
     */
    private nascimentoObrigatorioValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (valor) return null;

            const tipoInscricao = control.parent?.get('tipoInscricao')?.value;
            return tipoInscricao === 'I' ? null : { required: true };
        };
    }

    private suspensaoFimValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const fim = String(control.value ?? '').trim();
            if (!fim) {
                return null;
            }

            if (!DateUtils.isDataValida(fim)) {
                return { dataInvalida: true };
            }

            const inicio = String(this.dadosRegistroForm?.get('suspensaoInicio')?.value ?? '').trim();
            if (!inicio || !DateUtils.isDataValida(inicio)) {
                return null;
            }

            return this.dataInicioMenorQueFim(inicio, fim) ? null : { dataFimMenorOuIgualInicio: true };
        };
    }

    setupFormListeners(): void {
        this.dadosRegistroForm.statusChanges.subscribe(() => {
            this.formValid.set(this.dadosRegistroForm.valid);
        });

        this.dadosRegistroForm
            .get('tipoInscricao')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.dadosRegistroForm.get('nascimentoData')?.updateValueAndValidity());

        this.dadosRegistroForm.get('paisIdPais')?.valueChanges.subscribe((valor) => {
            const ehBrasil = Number(valor) === 204;
            this.paisEhBrasil.set(ehBrasil);

            if (ehBrasil) {
                this.dadosRegistroForm.patchValue({ nmMunicipioExterior: '' }, { emitEvent: false });
            } else {
                this.dadosRegistroForm.patchValue({ naturalidade: '', municipioIdMunicipio: null }, { emitEvent: false });
            }
        });

        const comDeficienciaCtrl = this.dadosRegistroForm.get('comDeficiencia');
        const valorInicialDeficiencia = this.normalizarSimNao(comDeficienciaCtrl?.value);
        this.comDeficienciaAnterior = valorInicialDeficiencia || 'N';
        this.mostrarSecaoDeficiencias.set(this.modoEdicao() || valorInicialDeficiencia === 'S');

        comDeficienciaCtrl?.valueChanges.subscribe((valor) => {
            const atual = this.normalizarSimNao(valor);
            const anterior = this.comDeficienciaAnterior;
            const temDeficiencia = atual === 'S';

            this.mostrarSecaoDeficiencias.set(this.modoEdicao() || temDeficiencia);

            if (!temDeficiencia) {
                this.mostrarDialogDeficiencia = false;
                this.limparDetalhesDeficiencia();
            } else if (this.modoEdicao() && anterior !== 'S') {
                this.abrirDialogDeficiencia(anterior);
            }

            this.comDeficienciaAnterior = atual || 'N';
        });

        const situacaoCtrl = this.dadosRegistroForm.get('situacao');
        this.previousSituacaoValue = situacaoCtrl?.value ?? null;

        this.dadosRegistroForm.get('suspensaoInicio')?.valueChanges.subscribe(() => {
            this.dadosRegistroForm.get('suspensaoFim')?.updateValueAndValidity({ emitEvent: false });
        });

        situacaoCtrl?.valueChanges.subscribe((raw) => {
            this.atualizarVisibilidadeSuspensao(raw);

            if (!this.modoEdicao()) {
                this.previousSituacaoValue = raw;
                return;
            }

            const atual = this.extrairCodigoSituacao(raw);
            const anterior = this.extrairCodigoSituacao(this.previousSituacaoValue);

            if (atual !== anterior && this.isSituacaoSuspensao(atual)) {
                this.pendingSituacaoValue = raw;
                this.snapshotSuspensao = this.criarSnapshotSuspensao();
                this.removerAnexoSuspensao();
                this.mostrarDialogSuspensao = true;
                return;
            }

            if (atual !== anterior && this.exigeDataMudancaSituacao(atual)) {
                this.pendingSituacaoValue = raw;
                this.snapshotMudancaSituacao = { dtMudancaSituacao: this.dtMudancaSituacao };
                this.dataMudancaSituacaoTemp = this.dtMudancaSituacao ?? null;
                this.situacaoMudancaDescricao = this.obterDescricaoSituacao(atual);
                this.mostrarDialogMudancaSituacao = true;
                return;
            }

            this.previousSituacaoValue = raw;
        });

        const tipoCtrl = this.dadosRegistroForm.get('tipoInscricao');
        tipoCtrl?.valueChanges.subscribe((valorAtual) => {
            if (!this.modoEdicao()) return;
            const atual = valorAtual ?? null;
            const anterior = this.previousTipoInscricao;

            if (anterior === null) {
                this.previousTipoInscricao = atual;
                return;
            }

            if (atual !== anterior) {
                this.pendingTipoInscricao = atual;
                this.dataAlteracaoTemp = null;
                this.showDataAlteracaoDialog = true;
            }
        });
    }

    abrirDialogDeficiencia(comDeficienciaAnterior?: string | null): void {
        if (this.mostrarDialogDeficiencia) return;
        this.snapshotDeficiencia = this.criarSnapshotDeficiencia(comDeficienciaAnterior);
        this.mostrarDialogDeficiencia = true;
    }

    confirmarDialogDeficiencia(): void {
        if (!this.validarDialogDeficiencia()) return;
        this.mostrarDialogDeficiencia = false;
        this.snapshotDeficiencia = undefined;
    }

    confirmarDialogSuspensao(): void {
        if (!this.validarDialogSuspensao()) return;
        const arquivo = this.arquivoSuspensao;
        if (!arquivo && !this.documentoSuspensaoBase64) {
            this.mostrarErroDeficiencia('Selecione um arquivo PDF para comprovar a mudanca da situacao.');
            return;
        }

        if (arquivo) {
            this.lerArquivoComoBase64(arquivo)
                .then((base64) => this.finalizarConfirmacaoSuspensao(base64))
                .catch((err) => {
                    this.mostrarErroDeficiencia('Falha ao processar o anexo da situacao.');
                    this.logger.error(err);
                });
            return;
        }

        this.finalizarConfirmacaoSuspensao(this.documentoSuspensaoBase64);
    }

    private finalizarConfirmacaoSuspensao(documentoBase64?: string): void {
        this.documentoSuspensaoBase64 = documentoBase64;
        this.mostrarDialogSuspensao = false;
        this.snapshotSuspensao = undefined;
        this.pendingSituacaoValue = null;
        this.previousSituacaoValue = this.dadosRegistroForm.get('situacao')?.value ?? null;
    }

    confirmarDialogMudancaSituacao(): void {
        const data = String(this.dataMudancaSituacaoTemp || '').trim();
        if (!DateUtils.isDataValida(data)) {
            this.messageService.add({ severity: 'error', summary: 'Aviso', detail: 'Informe uma data de mudanca de situacao valida.' });
            return;
        }

        if (FormularioValidatorUtil.dataMaiorQueHoje(data)) {
            this.messageService.add({ severity: 'error', summary: 'Aviso', detail: 'A data de mudanca de situacao deve ser menor ou igual a hoje.' });
            return;
        }

        this.dtMudancaSituacao = data;
        this.mostrarDialogMudancaSituacao = false;
        this.snapshotMudancaSituacao = undefined;
        this.pendingSituacaoValue = null;
        this.dataMudancaSituacaoTemp = null;
        this.situacaoMudancaDescricao = '';
        this.previousSituacaoValue = this.dadosRegistroForm.get('situacao')?.value ?? null;
    }

    cancelarDialogDeficiencia(): void {
        this.mostrarDialogDeficiencia = false;

        if (!this.snapshotDeficiencia) return;

        const snapshot = this.snapshotDeficiencia;
        this.dadosRegistroForm.patchValue(
            {
                comDeficiencia: snapshot.comDeficiencia,
                tipoDeficiencia: snapshot.tipoDeficiencia,
                origemDeficiencia: snapshot.origemDeficiencia,
                deficienciaAdaptada: snapshot.deficienciaAdaptada,
                deficiencias: snapshot.outrasInformacoes
            },
            { emitEvent: false }
        );

        this.comDeficienciaAnterior = this.normalizarSimNao(snapshot.comDeficiencia) || 'N';
        this.mostrarSecaoDeficiencias.set(this.modoEdicao() || this.comDeficienciaAnterior === 'S');
        this.snapshotDeficiencia = undefined;
    }

    cancelarDialogSuspensao(): void {
        this.mostrarDialogSuspensao = false;

        const situacaoCtrl = this.dadosRegistroForm.get('situacao');
        if (situacaoCtrl) {
            situacaoCtrl.setValue(this.previousSituacaoValue ?? '', { emitEvent: false });
        }

        if (this.snapshotSuspensao) {
            const snapshot = this.snapshotSuspensao;
            this.dadosRegistroForm.patchValue(
                {
                    suspensaoInicio: snapshot.suspensaoInicio,
                    suspensaoFim: snapshot.suspensaoFim,
                    observacao: snapshot.observacao,
                    suspensaoDivulgaPortal: snapshot.suspensaoDivulgaPortal
                },
                { emitEvent: false }
            );
            this.documentoSuspensaoBase64 = snapshot.documentoSuspensaoBase64;
            this.arquivoSuspensaoNome = snapshot.arquivoSuspensaoNome;
            this.arquivoSuspensao = null;
        }

        this.pendingSituacaoValue = null;
        this.snapshotSuspensao = undefined;
        this.atualizarVisibilidadeSuspensao(this.previousSituacaoValue);
    }

    cancelarDialogMudancaSituacao(): void {
        this.mostrarDialogMudancaSituacao = false;

        const situacaoCtrl = this.dadosRegistroForm.get('situacao');
        if (situacaoCtrl) {
            situacaoCtrl.setValue(this.previousSituacaoValue ?? '', { emitEvent: false });
        }

        this.dtMudancaSituacao = this.snapshotMudancaSituacao?.dtMudancaSituacao;
        this.pendingSituacaoValue = null;
        this.snapshotMudancaSituacao = undefined;
        this.dataMudancaSituacaoTemp = null;
        this.situacaoMudancaDescricao = '';
        this.atualizarVisibilidadeSuspensao(this.previousSituacaoValue);
    }

    private criarSnapshotDeficiencia(comDeficienciaAnterior?: string | null): DeficienciaSnapshot {
        const valores = this.dadosRegistroForm.value;
        return {
            comDeficiencia: this.normalizarSimNao(comDeficienciaAnterior ?? valores.comDeficiencia) || 'N',
            tipoDeficiencia: valores.tipoDeficiencia ?? '',
            origemDeficiencia: valores.origemDeficiencia ?? '',
            deficienciaAdaptada: valores.deficienciaAdaptada ?? '',
            outrasInformacoes: valores.deficiencias ?? ''
        };
    }

    private limparDetalhesDeficiencia(): void {
        this.dadosRegistroForm.patchValue(
            {
                tipoDeficiencia: '',
                origemDeficiencia: '',
                deficienciaAdaptada: '',
                deficiencias: ''
            },
            { emitEvent: false }
        );
    }

    private validarDialogDeficiencia(): boolean {
        const origem = this.normalizarSimNao(this.dadosRegistroForm.get('origemDeficiencia')?.value);
        if (!origem) {
            this.mostrarErroDeficiencia('É necessário preencher a origem da deficiência!');
            this.focarCampoDeficiencia('origem');
            return false;
        }

        const tipo = this.normalizarSimNao(this.dadosRegistroForm.get('tipoDeficiencia')?.value);
        const outrasInformacoes = String(this.dadosRegistroForm.get('deficiencias')?.value || '').trim();
        if (tipo === 'O' && !outrasInformacoes) {
            this.mostrarErroDeficiencia('É necessário descrever a deficiência do médico!');
            this.focarCampoDeficiencia('outras');
            return false;
        }

        const adaptado = this.normalizarSimNao(this.dadosRegistroForm.get('deficienciaAdaptada')?.value);
        if (!adaptado) {
            this.mostrarErroDeficiencia('É necessário preencher se o médico está adaptado!');
            this.focarCampoDeficiencia('adaptada');
            return false;
        }

        return true;
    }

    private validarDialogSuspensao(): boolean {
        const inicio = String(this.dadosRegistroForm.get('suspensaoInicio')?.value || '').trim();
        if (!inicio) {
            this.mostrarErroDeficiencia('É necessário preencher a data de início da suspensão!');
            this.focarCampoSuspensao('inicio');
            return false;
        }

        if (FormularioValidatorUtil.dataMaiorQueHoje(inicio)) {
            this.mostrarErroDeficiencia('A data de inicio da suspensao deve ser menor ou igual a hoje.');
            this.focarCampoSuspensao('inicio');
            return false;
        }

        const fim = String(this.dadosRegistroForm.get('suspensaoFim')?.value || '').trim();
        if (fim && !this.dataInicioMenorQueFim(inicio, fim)) {
            this.mostrarErroDeficiencia('A data de inicio da suspensao deve ser inferior a data fim.');
            this.focarCampoSuspensao('fim');
            return false;
        }

        const observacao = String(this.dadosRegistroForm.get('observacao')?.value || '').trim();
        if (!observacao) {
            this.mostrarErroDeficiencia('É necessário preencher a observação da suspensão!');
            this.focarCampoSuspensao('observacao');
            return false;
        }

        return true;
    }

    private mostrarErroDeficiencia(mensagem: string): void {
        this.messageService.add({ severity: 'error', summary: 'Aviso', detail: mensagem });
    }

    private dataInicioMenorQueFim(inicio: string, fim: string): boolean {
        const dataInicio = this.parseDataBr(inicio);
        const dataFim = this.parseDataBr(fim);

        if (!dataInicio || !dataFim) {
            return false;
        }

        return dataInicio.getTime() < dataFim.getTime();
    }

    private parseDataBr(valor: string): Date | null {
        const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(valor);
        if (!match) {
            return null;
        }

        const [, dia, mes, ano] = match;
        const data = new Date(Number(ano), Number(mes) - 1, Number(dia));
        data.setHours(0, 0, 0, 0);

        if (data.getFullYear() !== Number(ano) || data.getMonth() !== Number(mes) - 1 || data.getDate() !== Number(dia)) {
            return null;
        }

        return data;
    }

    private focarCampoDeficiencia(campo: 'tipo' | 'origem' | 'adaptada' | 'outras'): void {
        setTimeout(() => {
            if (campo === 'outras') {
                this.deficienciaOutras()?.nativeElement?.focus();
                return;
            }

            if (campo === 'origem') {
                this.origemDeficienciaSelect()?.focus();
                return;
            }

            if (campo === 'adaptada') {
                this.deficienciaAdaptadaSelect()?.focus();
                return;
            }

            this.tipoDeficienciaSelect()?.focus();
        });
    }

    private focarCampoSuspensao(campo: 'inicio' | 'fim' | 'observacao'): void {
        setTimeout(() => {
            if (campo === 'observacao') {
                this.observacaoSuspensaoInput()?.nativeElement?.focus();
                return;
            }

            if (campo === 'fim') {
                this.focarDatePicker(this.suspensaoFimInput());
                return;
            }

            this.focarDatePicker(this.suspensaoInicioInput());
        });
    }

    private focarDatePicker(datePicker?: DatePicker): void {
        datePicker?.inputfieldViewChild?.nativeElement?.focus();
    }

    classeDatePickerSuspensao(nomeCampo: 'suspensaoInicio' | 'suspensaoFim'): string {
        const classes = !this.modoEdicao() ? 'w-full readonly-input' : 'w-full';
        return this.campoSuspensaoInvalido(nomeCampo) ? `${classes} p-invalid` : classes;
    }

    campoSuspensaoInvalido(nomeCampo: 'suspensaoInicio' | 'suspensaoFim'): boolean {
        const control = this.dadosRegistroForm.get(nomeCampo);
        return !!control && control.invalid && (control.dirty || control.touched);
    }

    mensagemErroSuspensao(nomeCampo: 'suspensaoInicio' | 'suspensaoFim'): string {
        const control = this.dadosRegistroForm.get(nomeCampo);
        if (control?.hasError('dataInvalida')) {
            return 'Data invalida.';
        }

        if (control?.hasError('dataMaiorQueHojeInvalida')) {
            return 'A data de inicio da suspensao deve ser menor ou igual a hoje.';
        }

        if (control?.hasError('dataFimMenorOuIgualInicio')) {
            return 'A data fim deve ser superior a data de inicio.';
        }

        return 'Campo invalido.';
    }

    private normalizarSimNao(valor: unknown): string {
        return (valor ?? '').toString().trim().toUpperCase();
    }

    private extrairCodigoSituacao(raw: unknown): string | undefined {
        if (raw == null) return undefined;
        if (typeof raw === 'string') return raw.trim().toUpperCase();
        if (typeof raw === 'object') {
            const obj = raw as { value?: unknown; codigo?: unknown; idMedSituacao?: unknown };
            const possible = obj.value ?? obj.codigo ?? obj.idMedSituacao;
            if (possible != null) {
                return String(possible).trim().toUpperCase();
            }
        }
        return undefined;
    }

    private isSituacaoSuspensao(code?: string): boolean {
        if (!code) return false;
        const permitidos = new Set(['C', 'S', 'R', 'X', 'N', 'I', 'J', 'O', 'B', 'M']);
        return permitidos.has(code);
    }

    private exigeDataMudancaSituacao(code?: string): boolean {
        if (!code) return false;
        const permitidos = new Set(['T', 'P', 'F', 'L', 'U', 'E', 'G']);
        return permitidos.has(code);
    }

    private obterDescricaoSituacao(code?: string): string {
        if (!code) return '';
        const found = this.opcoesSituacao().find((op) => String(op.value).trim().toUpperCase() === code);
        return found?.label ?? code;
    }

    private criarSnapshotSuspensao(): SuspensaoSnapshot {
        return {
            suspensaoInicio: this.dadosRegistroForm.get('suspensaoInicio')?.value ?? '',
            suspensaoFim: this.dadosRegistroForm.get('suspensaoFim')?.value ?? '',
            observacao: this.dadosRegistroForm.get('observacao')?.value ?? '',
            suspensaoDivulgaPortal: this.normalizarSimNao(this.dadosRegistroForm.get('suspensaoDivulgaPortal')?.value) || 'N',
            documentoSuspensaoBase64: this.documentoSuspensaoBase64,
            arquivoSuspensaoNome: this.arquivoSuspensaoNome
        };
    }

    private atualizarVisibilidadeSuspensao(raw: unknown): void {
        const code = this.extrairCodigoSituacao(raw);
        this.mostrarAreaSuspensao.set(this.isSituacaoSuspensao(code));
    }

    private carregarDominios(): void {
        this.carregandoDominios.set(true);

        forkJoin({
            tiposInscricao: this.dadosRegistroService.listarTiposInscricao().pipe(catchError(() => of([]))),
            tiposLicenca: this.dadosRegistroService.listarTiposLicenca().pipe(catchError(() => of([]))),
            estadosCivis: this.dadosRegistroService.listarEstadosCivis().pipe(catchError(() => of([]))),
            paises: this.paisService.listarPaises().pipe(catchError(() => of([])))
        })
            .pipe(finalize(() => this.carregandoDominios.set(false)))
            .subscribe({
                next: (resultados) => {
                    this.opcoesTipoInscricao = resultados.tiposInscricao.map((item) => ({
                        label: item.descricao,
                        value: item.codigo
                    }));

                    this.opcoesTipoLicenca = resultados.tiposLicenca.map((item) => ({
                        label: item.descricao,
                        value: item.codigo
                    }));

                    this.opcoesEstadoCivil = resultados.estadosCivis.map((item) => ({
                        label: item.descricao,
                        value: item.codigo
                    }));

                    this.opcoesPaises = resultados.paises.map((p) => ({ label: p.nmPais, value: Number(p.id) })).sort((a, b) => a.label.localeCompare(b.label));

                    this.definirPaisPadrao(resultados.paises);
                },
                error: (err) => this.logger.error('Erro crítico ao carregar domínios do formulário', err)
            });
    }

    private definingPaisPadrao(listaPaises: PaisResponse[]): void {
        const ctrl = this.dadosRegistroForm.get('paisIdPais');
        if (ctrl && (ctrl.value === null || ctrl.value === undefined)) {
            const br = listaPaises.find((p) => (p.sgPais || '').toUpperCase() === 'BR');
            const padrao = br?.id ?? this.opcoesPaises[0]?.value ?? null;
            if (padrao !== null) {
                ctrl.setValue(Number(padrao), { emitEvent: false });
                this.paisEhBrasil.set(Number(padrao) === 204);
            }
        }
    }

    private definirPaisPadrao(listaPaises: PaisResponse[]): void {
        this.definingPaisPadrao(listaPaises);
    }

    habilitarEdicao(): void {
        this.modoEdicao.set(true);
        this.dadosRegistroForm.enable({ emitEvent: false });
        this.atualizarEstadoDisabledControlesEdicao();
        this.previousSituacaoValue = this.dadosRegistroForm.get('situacao')?.value ?? null;

        this.dadosRegistroForm.get('comDeficiencia')?.updateValueAndValidity({ emitEvent: true });
        this.formValid.set(this.dadosRegistroForm.valid);
    }

    salvarDados(): void {
        if (!this.dadosRegistroForm.valid) return;

        const formValues = this.dadosRegistroForm.value;

        const medicoFormValue = {
            ...formValues,
            inCertidaoEtica: formValues.emitirCertEtica ? 'S' : 'N',
            inTipoSangue: formValues.tipoSanguineo ?? undefined,
            inFatorRh: this.mapper.unmapRh(formValues.rh),
            inDoador: this.mapper.unmapDoacaoOrgaos(formValues.doacaoOrgaos),
            inCategoria: formValues.categoriaMilitar ?? undefined,
            inEstrangeiro: formValues.nacionalidade,
            dtAlteracaoTipoInscricao: this.dtAlteracaoTipoInscricao ?? undefined,
            dtMudancaSituacao: this.dtMudancaSituacao ?? undefined,
            suspensaoDivulgaPortal: this.normalizarSimNao(formValues.suspensaoDivulgaPortal) || undefined,
            documentoSuspensaoBase64: this.documentoSuspensaoBase64 ?? undefined
        };

        const pessoaPartial: Partial<PessoaSaveRequest> = {
            nome: this.dadosRegistroForm.get('nome')?.value || undefined,
            nomeSocial: this.dadosRegistroForm.get('nomeSocial')?.value ?? ''
        };

        this.salvar.emit({ pessoaPartial, medicoFormValue });
    }

    cancelarEdicao(): void {
        this.modoEdicao.set(false);
        this.dadosRegistroForm.disable({ emitEvent: false });
        this.atualizarEstadoDisabledControlesEdicao();
        this.resetarEdicaoCensuraPublica(true);
        this.mostrarDialogDeficiencia = false;
        this.mostrarDialogMudancaSituacao = false;
        this.mostrarDialogSuspensao = false;
        this.mostrarDialogCensuraPublica = false;
        this.snapshotDeficiencia = undefined;
        this.snapshotSuspensao = undefined;
        this.snapshotMudancaSituacao = undefined;
        this.pendingSituacaoValue = null;
        this.dataMudancaSituacaoTemp = null;
        this.situacaoMudancaDescricao = '';
        this.removerAnexoSuspensao();

        const pessoaAtual = this.pessoa();
        if (this.isPessoaRequest(pessoaAtual)) {
            this.carregarMedico(pessoaAtual);
        }

        this.dadosRegistroForm.get('comDeficiencia')?.updateValueAndValidity({ emitEvent: true });
    }

    private atualizarEstadoDisabledControlesEdicao(): void {
        const isEdit = this.modoEdicao();
        const municipioCtrl = this.dadosRegistroForm.get('municipioIdMunicipio');
        const emitirCertEticaCtrl = this.dadosRegistroForm.get('emitirCertEtica');

        // Mantém o ToggleSwitch visualmente ativo em modo leitura; a interação segue bloqueada via [readonly] no template.
        emitirCertEticaCtrl?.enable({ emitEvent: false });

        if (municipioCtrl) {
            if (isEdit) {
                municipioCtrl.enable({ emitEvent: false });
            } else {
                municipioCtrl.disable({ emitEvent: false });
            }
        }
    }

    private carregarSituacoesIniciais(): void {
        this.dadosRegistroService.listarSituacoes().subscribe({
            next: (situacoes) => {
                this.opcoesSituacao.set(
                    situacoes.map((item) => ({
                        label: item.dsMedSituacao,
                        value: item.idMedSituacao
                    }))
                );
            },
            error: (err) => this.logger.error('Erro ao carregar situações iniciais:', err)
        });
    }

    private carregarMedico(pessoa: Pessoa): void {
        const request: Pessoa = { id: pessoa.id, uf: pessoa.uf };

        this.carregarSuspensoes(request);
        this.carregarDeficiencias(request);
        this.carregarCensurasPublicas(request);

        this.medicoService.buscar(request).subscribe({
            next: (medico: MedicoRequest) => {
                if (medico.inSituacao) {
                    this.dadosRegistroService.listarSituacoesPossiveis(medico.inSituacao).subscribe({
                        next: (situacoes) => {
                            this.opcoesSituacao.set(
                                situacoes.map((item) => ({
                                    label: item.dsMedSituacao,
                                    value: item.idMedSituacao
                                }))
                            );
                            this.preencherFormulario(medico);

                            const situacaoCode = medico.inSituacao?.toString().trim().toUpperCase();
                            if (situacaoCode) {
                                const control = this.dadosRegistroForm?.get('situacao');
                                if (control) control.setValue(situacaoCode, { emitEvent: true });
                            }
                        },
                        error: () => this.preencherFormulario(medico)
                    });
                } else {
                    this.preencherFormulario(medico);
                }
            },
            error: (err) => this.logger.error('Erro ao buscar dados do médico:', err)
        });
    }

    private preencherFormulario(m: MedicoRequest): void {
        if (!this.dadosRegistroForm) return;

        this.dadosRegistroForm.patchValue(
            {
                nomePai: m.nmPai ?? '',
                nomeMae: m.nmMae ?? '',
                genero: m.inSexo ?? 'M',
                estadoCivil: m.inEstadoCivil,
                nascimentoData: m.dtNascimento ?? '',
                paisIdPais: m.paisIdPais != null ? Number(m.paisIdPais) : null,
                nmMunicipioExterior: m.nmMunicipioExterior ?? '',
                emitirCertEtica: m.inCertidaoEtica === 'S',
                nacionalidade: m.inEstrangeiro ?? 'B',
                situacao: m.inSituacao,
                tipoInscricao: m.inTipoInscricao,
                tipoLicenca: m.inTipoLicenca,
                categoriaMilitar: this.mapper.mapCategoriaMilitar(m.inCategoria) ?? this.dadosRegistroForm.get('categoriaMilitar')?.value ?? 'C',
                decMilitarData: this.mapper.formatarData(m.dtValCertificadoMilitar),
                tituloEleitorNumero: m.nuTituloEleitor ?? '',
                tituloEleitorZona: m.dsZona ?? '',
                tituloEleitorSecao: m.dsSecao ?? '',
                tituloEleitorUF: m.sgUfTituloEleitor ?? '',
                documentoNumero: m.nuRg ?? '',
                documentoDataEmissao: m.dtExpedicaoRg ?? '',
                documentoOrgaoEmissor: m.nmOrgaoExpedidor ?? '',
                documentoUF: m.sgUfRgExpedida ?? '',
                tipoSanguineo: m.inTipoSangue ?? '',
                rh: this.mapper.mapRh(m.inFatorRh) ?? '',
                doacaoOrgaos: this.mapper.mapDoacaoOrgaos(m.inDoador) ?? '',
                municipioIdMunicipio: this.mapper.extrairMunicipioId(m)
            },
            { emitEvent: false }
        );

        const paisId = Number(this.dadosRegistroForm.get('paisIdPais')?.value);
        this.paisEhBrasil.set(paisId === 204);

        this.ajustarSelecoesCaseInsensitive(m);

        if (!this.modoEdicao()) {
            this.dadosRegistroForm.disable({ emitEvent: false });
        }
        this.atualizarEstadoDisabledControlesEdicao();

        this.atualizarVisibilidadeSuspensao(this.dadosRegistroForm.get('situacao')?.value);

        const tipoAtual = this.dadosRegistroForm.get('tipoInscricao')?.value ?? null;
        this.previousTipoInscricao = tipoAtual;
        this.pendingTipoInscricao = null;
        this.dtAlteracaoTipoInscricao = undefined;
        this.dtMudancaSituacao = undefined;
        this.removerAnexoSuspensao();
        this.comDeficienciaAnterior = this.normalizarSimNao(this.dadosRegistroForm.get('comDeficiencia')?.value) || 'N';
        this.previousSituacaoValue = this.dadosRegistroForm.get('situacao')?.value ?? null;
    }

    onSelectSuspensaoPdf(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        if (!file) return;

        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        const maxBytes = 10 * 1024 * 1024;

        if (!isPdf) {
            this.mostrarErroDeficiencia('Arquivo invalido. Selecione um PDF.');
            input.value = '';
            return;
        }

        if (file.size > maxBytes) {
            this.mostrarErroDeficiencia('Arquivo muito grande. Tamanho maximo 10 MB.');
            input.value = '';
            return;
        }

        this.arquivoSuspensao = file;
        this.arquivoSuspensaoNome = file.name;
        this.documentoSuspensaoBase64 = undefined;
        input.value = '';
    }

    removerAnexoSuspensao(): void {
        this.arquivoSuspensao = null;
        this.arquivoSuspensaoNome = '';
        this.documentoSuspensaoBase64 = undefined;
        const input = this.suspensaoPdfInput()?.nativeElement;
        if (input) {
            input.value = '';
        }
    }

    confirmarDataAlteracao(): void {
        if (!this.dataAlteracaoTemp) return;
        if (FormularioValidatorUtil.dataMaiorQueHoje(this.dataAlteracaoTemp)) {
            this.messageService.add({ severity: 'error', summary: 'Aviso', detail: 'A data da alteracao deve ser menor ou igual a hoje.' });
            return;
        }
        this.dtAlteracaoTipoInscricao = this.dataAlteracaoTemp;
        const atual = this.pendingTipoInscricao ?? null;
        this.previousTipoInscricao = atual;
        this.showDataAlteracaoDialog = false;
        this.pendingTipoInscricao = null;
        this.dataAlteracaoTemp = null;
    }

    cancelarDataAlteracao(): void {
        const tipoCtrl = this.dadosRegistroForm.get('tipoInscricao');
        if (tipoCtrl) {
            tipoCtrl.setValue(this.previousTipoInscricao ?? '', { emitEvent: false });
        }
        this.pendingTipoInscricao = null;
        this.dataAlteracaoTemp = null;
        this.showDataAlteracaoDialog = false;
    }

    private ajustarSelecoesCaseInsensitive(m: MedicoRequest): void {
        const situacaoBusca = m.inSituacao || m.dsSituacao;
        if (situacaoBusca) {
            const found = this.opcoesSituacao().find((op) => String(op.value).toLowerCase() === String(situacaoBusca).toLowerCase() || op.label.toLowerCase() === String(situacaoBusca).toLowerCase());
            if (found) this.dadosRegistroForm.get('situacao')?.setValue(found.value, { emitEvent: false });
        }
    }

    private preencherNomeDaPessoa(pessoaAtual: Pessoa | PessoaResponseModel | undefined): void {
        if (!this.dadosRegistroForm || !pessoaAtual) return;

        const anyPessoa = pessoaAtual as { nome?: string; nomeSocial?: string; nomes?: Record<string, string | undefined> };

        let nome: string | undefined = anyPessoa?.nome;
        let nomeSocial: string | undefined = anyPessoa?.nomeSocial;

        const nomes = anyPessoa?.nomes;
        if (nomes && typeof nomes === 'object') {
            nome = nome ?? nomes['nome'] ?? nomes['Nome'] ?? nomes['nomeCompleto'] ?? nomes['NomeCompleto'];
            nomeSocial = nomeSocial ?? nomes['nomeSocial'] ?? nomes['NomeSocial'] ?? nomes['Nome Social'];
        }

        if (nome || nomeSocial) {
            this.dadosRegistroForm.patchValue(
                {
                    nome: nome ?? '',
                    nomeSocial: nomeSocial ?? ''
                },
                { emitEvent: false }
            );
        }
    }

    private isPessoaRequest(pessoa: Pessoa | PessoaResponseModel | undefined): pessoa is Pessoa {
        return !!pessoa && typeof (pessoa as { id?: unknown }).id === 'number' && typeof (pessoa as { uf?: unknown }).uf === 'string';
    }

    private atualizarHabilitacaoAcoes(pessoaAtual: Pessoa | PessoaResponseModel | undefined): void {
        const ufUsuario = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        const ufPessoa = String((pessoaAtual as { uf?: string })?.uf ?? '')
            .trim()
            .toUpperCase();

        const deveDesabilitar = !!ufUsuario && !!ufPessoa && ufUsuario !== 'BR' && ufUsuario !== ufPessoa;
        this.acoesDesabilitadas.set(deveDesabilitar);
    }

    private carregarSuspensoes(pessoa: Pessoa): void {
        this.loadingSuspensoes.set(true);
        this.erroSuspensoes.set(undefined);
        this.suspensoes.set([]);
        this.dadosRegistroService
            .listarSuspensoes(pessoa)
            .pipe(finalize(() => this.loadingSuspensoes.set(false)))
            .subscribe({
                next: (resp) => {
                    this.suspensoes.set(
                        (resp || []).map((s) => ({
                            ...s,
                            dtInicio: this.mapper.formatarData(s.dtInicio),
                            dtFinal: this.mapper.formatarData(s.dtFinal)
                        }))
                    );
                },
                error: (err) => {
                    this.erroSuspensoes.set('Falha ao carregar períodos de suspensão.');
                    this.logger.error(err);
                }
            });
    }

    baixarAnexoSuspensao(item: MedicoPeriodoResponse): void {
        const base64Raw = this.obterDocumentoSuspensaoBase64(item);
        if (!base64Raw) {
            this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Nenhum anexo disponivel para download.' });
            return;
        }

        try {
            const { data, mime } = this.extrairBase64(base64Raw);
            const mimeFinal = mime ?? this.inferirMimeType(data);
            const blob = this.base64ToBlob(data, mimeFinal);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = this.nomeArquivoAnexoSuspensao(item, mimeFinal);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao baixar anexo.' });
            this.logger.error('Falha ao baixar anexo do registro do médico', err);
        }
    }

    private obterDocumentoSuspensaoBase64(item: MedicoPeriodoResponse): string {
        return String(item.documentoBase64 || item.documentoSuspensaoBase64 || item.imDocumento || '').trim();
    }

    private nomeArquivoAnexoSuspensao(item: MedicoPeriodoResponse, mime: string): string {
        const nomeDocumento = String(item.nmDocumento || '').trim();
        if (nomeDocumento) {
            return nomeDocumento;
        }

        const dataInicio = String(item.dtInicio || '').replace(/\D/g, '') || 'periodo';
        const extensao = mime === 'application/pdf' ? 'pdf' : 'bin';
        return `anexo-suspensao-${dataInicio}.${extensao}`;
    }

    private carregarDeficiencias(pessoa: Pessoa): void {
        this.dadosRegistroService.listarDeficiencias(pessoa).subscribe({
            next: (resp) => this.aplicarDeficienciaResposta(resp),
            error: (err) => this.logger.error('Falha ao carregar deficiências do médico.', err)
        });
    }

    private aplicarDeficienciaResposta(resp?: MedicoDeficienciaResponse[] | null): void {
        const lista = Array.isArray(resp) ? resp : [];
        const primeiro = lista[0];
        const possuiDeficiencia = lista.length > 0;

        this.dadosRegistroForm.patchValue(
            {
                comDeficiencia: possuiDeficiencia ? 'S' : 'N',
                tipoDeficiencia: primeiro?.inTipoDeficiencia ?? '',
                origemDeficiencia: primeiro?.inOrigemDeficiencia ?? '',
                deficienciaAdaptada: primeiro?.inAdaptadoDeficiencia ?? '',
                deficiencias: primeiro?.txOutraDeficiencia ?? ''
            },
            { emitEvent: false }
        );

        this.comDeficienciaAnterior = possuiDeficiencia ? 'S' : 'N';
        this.mostrarSecaoDeficiencias.set(this.modoEdicao() || possuiDeficiencia);
    }

    salvarCensuraPublica(): void {
        if (this.editandoCensuraPublica()) {
            this.atualizarCensuraPublica();
            return;
        }

        this.inserirCensuraPublica();
    }

    abrirDialogCensuraPublica(): void {
        if (!this.modoEdicao()) return;
        this.resetarEdicaoCensuraPublica(true);
        this.erroCensurasPublicas.set(undefined);
        this.mostrarDialogCensuraPublica = true;
    }

    iniciarEdicaoCensuraPublica(item: CensuraPublicaItem): void {
        if (!this.modoEdicao()) return;
        this.editandoCensuraPublica.set(true);
        this.censuraPublicaChave.set(item.dtInicio);
        this.removerAnexoCensuraPublica();
        this.erroCensurasPublicas.set(undefined);

        this.dadosRegistroForm.patchValue(
            {
                censuraPublicaInicio: item.dtInicio ?? '',
                censuraPublicaFim: item.dtFim ?? '',
                censuraPublicaObservacao: item.observacao ?? '',
                censuraPublicaDivulgarPortal: item.inDivulgaPortal ?? 'N'
            },
            { emitEvent: false }
        );

        this.dadosRegistroForm.get('censuraPublicaInicio')?.disable({ emitEvent: false });
        this.mostrarDialogCensuraPublica = true;
    }

    cancelarEdicaoCensuraPublica(): void {
        this.resetarEdicaoCensuraPublica(true);
    }

    cancelarDialogCensuraPublica(): void {
        this.resetarEdicaoCensuraPublica(true);
        this.mostrarDialogCensuraPublica = false;
        this.erroCensurasPublicas.set(undefined);
    }

    visualizarAnexoCensuraPublica(item: CensuraPublicaItem): void {
        const base64Raw = String(item.documentoBase64 || '').trim();
        if (!base64Raw) {
            this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Nenhum anexo disponivel.' });
            return;
        }

        try {
            const { data, mime } = this.extrairBase64(base64Raw);
            const mimeFinal = mime ?? this.inferirMimeType(data);
            const blob = this.base64ToBlob(data, mimeFinal);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao abrir anexo.' });
            this.logger.error('Falha ao abrir anexo do registro do médico', err);
        }
    }

    onSelectCensuraPublicaPdf(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        if (!file) return;

        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        const maxBytes = 10 * 1024 * 1024;

        if (!isPdf) {
            this.erroCensurasPublicas.set('Arquivo invalido. Selecione um PDF.');
            input.value = '';
            return;
        }

        if (file.size > maxBytes) {
            this.erroCensurasPublicas.set('Arquivo muito grande. Tamanho maximo 10 MB.');
            input.value = '';
            return;
        }

        this.arquivoCensuraPublica = file;
        this.arquivoCensuraPublicaNome = file.name;
        this.erroCensurasPublicas.set(undefined);
        input.value = '';
    }

    removerAnexoCensuraPublica(): void {
        this.arquivoCensuraPublica = null;
        this.arquivoCensuraPublicaNome = '';
        const input = this.censuraPdfInput()?.nativeElement;
        if (input) {
            input.value = '';
        }
    }

    private inserirCensuraPublica(): void {
        if (this.salvandoCensuraPublica()) return;
        const pessoaAtual = this.pessoa();
        if (!this.isPessoaRequest(pessoaAtual)) {
            this.erroCensurasPublicas.set('Pessoa n????o informada para inserir censura p????blica.');
            return;
        }

        const dtInicioRaw = this.dadosRegistroForm.get('censuraPublicaInicio')?.value ?? '';
        const dtFimRaw = this.dadosRegistroForm.get('censuraPublicaFim')?.value ?? '';
        const observacaoRaw = this.dadosRegistroForm.get('censuraPublicaObservacao')?.value ?? '';
        const divulgarPortal = this.normalizarSimNao(this.dadosRegistroForm.get('censuraPublicaDivulgarPortal')?.value);

        if (divulgarPortal !== 'S' && divulgarPortal !== 'N') {
            this.erroCensurasPublicas.set('Informe se a censura deve divulgar no portal.');
            return;
        }

        const dtInicio = this.validarDataCensura(dtInicioRaw);
        if (!dtInicio) {
            this.erroCensurasPublicas.set('Informe uma data de in????cio v????lida.');
            return;
        }
        if (FormularioValidatorUtil.dataMaiorQueHoje(dtInicio)) {
            this.erroCensurasPublicas.set('A data de inicio deve ser menor ou igual a hoje.');
            return;
        }

        const dtFim = dtFimRaw ? this.validarDataCensura(dtFimRaw) : undefined;
        if (dtFimRaw && !dtFim) {
            this.erroCensurasPublicas.set('Informe uma data de fim v????lida.');
            return;
        }

        const observacao = String(observacaoRaw || '').trim();
        const arquivo = this.arquivoCensuraPublica;

        if (!arquivo) {
            this.erroCensurasPublicas.set('Selecione um arquivo PDF para anexar.');
            return;
        }

        this.salvandoCensuraPublica.set(true);
        this.erroCensurasPublicas.set(undefined);

        this.lerArquivoComoBase64(arquivo)
            .then((base64) => {
                const body: MedicoCensuraPublicaRequest = {
                    idMedico: pessoaAtual.id,
                    uf: pessoaAtual.uf,
                    ufSgUfMedico: pessoaAtual.uf,
                    dtInicio,
                    inSuspConv: 'P',
                    inDivulgaPortal: divulgarPortal,
                    documentoBase64: base64,
                    ...(dtFim ? { dtFinal: dtFim } : {}),
                    ...(observacao ? { txObservacao: observacao } : {})
                };

                this.dadosRegistroService
                    .inserirCensuraPublica(body)
                    .pipe(finalize(() => this.salvandoCensuraPublica.set(false)))
                    .subscribe({
                        next: () => {
                            this.limparCamposCensuraPublica();
                            this.mostrarDialogCensuraPublica = false;
                            this.carregarCensurasPublicas(pessoaAtual);
                        },
                        error: (err) => {
                            this.erroCensurasPublicas.set(err?.message || 'Falha ao inserir censura p????blica.');
                            this.logger.error(err);
                        }
                    });
            })
            .catch((err) => {
                this.salvandoCensuraPublica.set(false);
                this.erroCensurasPublicas.set('Falha ao processar o anexo.');
                this.logger.error(err);
            });
    }

    private atualizarCensuraPublica(): void {
        if (this.salvandoCensuraPublica()) return;
        const pessoaAtual = this.pessoa();
        if (!this.isPessoaRequest(pessoaAtual)) {
            this.erroCensurasPublicas.set('Pessoa não informada para atualizar censura pública.');
            return;
        }

        const chaveInicio = this.censuraPublicaChave() ?? this.dadosRegistroForm.get('censuraPublicaInicio')?.value ?? '';
        if (this.validarDataCensura(chaveInicio) && FormularioValidatorUtil.dataMaiorQueHoje(chaveInicio)) {
            this.erroCensurasPublicas.set('A data de inicio deve ser menor ou igual a hoje.');
            return;
        }
        if (!this.validarDataCensura(chaveInicio)) {
            this.erroCensurasPublicas.set('Informe uma data de início válida.');
            return;
        }

        const dtFimRaw = this.dadosRegistroForm.get('censuraPublicaFim')?.value ?? '';
        if (dtFimRaw && !this.validarDataCensura(dtFimRaw)) {
            this.erroCensurasPublicas.set('Informe uma data de fim válida.');
            return;
        }

        const observacaoRaw = this.dadosRegistroForm.get('censuraPublicaObservacao')?.value ?? '';
        const observacao = String(observacaoRaw || '').trim();
        const divulgarPortal = this.normalizarSimNao(this.dadosRegistroForm.get('censuraPublicaDivulgarPortal')?.value);

        if (divulgarPortal !== 'S' && divulgarPortal !== 'N') {
            this.erroCensurasPublicas.set('Informe se a censura deve divulgar no portal.');
            return;
        }

        const body: MedicoCensuraPublicaUpdateRequest = {
            idMedico: pessoaAtual.id,
            uf: pessoaAtual.uf,
            dtInicio: chaveInicio,
            dtFinal: dtFimRaw ? dtFimRaw : null,
            txObservacao: observacao ? observacao : null,
            documentoBase64: null,
            inDivulgaPortal: divulgarPortal
        };

        this.salvandoCensuraPublica.set(true);
        this.erroCensurasPublicas.set(undefined);

        this.dadosRegistroService
            .atualizarCensuraPublica(body)
            .pipe(finalize(() => this.salvandoCensuraPublica.set(false)))
            .subscribe({
                next: () => {
                    this.resetarEdicaoCensuraPublica(true);
                    this.mostrarDialogCensuraPublica = false;
                    this.carregarCensurasPublicas(pessoaAtual);
                },
                error: (err) => {
                    this.erroCensurasPublicas.set(err?.message || 'Falha ao atualizar censura pública.');
                    this.logger.error(err);
                }
            });
    }

    private carregarCensurasPublicas(pessoa: Pessoa): void {
        this.loadingCensurasPublicas.set(true);
        this.erroCensurasPublicas.set(undefined);
        this.censurasPublicas.set([]);
        this.dadosRegistroService
            .listarCensurasPublicas(pessoa)
            .pipe(finalize(() => this.loadingCensurasPublicas.set(false)))
            .subscribe({
                next: (resp) => {
                    this.censurasPublicas.set(
                        (resp || []).map((c) => {
                            const documentoBase64 = c.documentoBase64 ?? undefined;
                            return {
                                dtInicio: this.mapper.formatarData(c.dtInicio),
                                dtFim: this.mapper.formatarData(c.dtFinal),
                                observacao: c.txObservacao ?? '',
                                documentoBase64,
                                possuiAnexo: !!(documentoBase64 && documentoBase64.trim()),
                                inDivulgaPortal: c.inDivulgaPortal ?? undefined
                            };
                        })
                    );
                },
                error: (err) => {
                    this.erroCensurasPublicas.set('Falha ao carregar censuras publicas.');
                    this.logger.error(err);
                }
            });
    }

    private validarDataCensura(valor?: string): string | undefined {
        if (!valor) return undefined;
        const trimmed = valor.trim();
        const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
        if (!m) return undefined;
        return trimmed;
    }

    private limparCamposCensuraPublica(): void {
        this.dadosRegistroForm.patchValue(
            {
                censuraPublicaInicio: '',
                censuraPublicaFim: '',
                censuraPublicaObservacao: '',
                censuraPublicaDivulgarPortal: 'N'
            },
            { emitEvent: false }
        );
        this.removerAnexoCensuraPublica();
    }

    private resetarEdicaoCensuraPublica(limparCampos: boolean): void {
        this.editandoCensuraPublica.set(false);
        this.censuraPublicaChave.set(null);
        if (limparCampos) {
            this.limparCamposCensuraPublica();
        }
        if (this.modoEdicao()) {
            this.dadosRegistroForm.get('censuraPublicaInicio')?.enable({ emitEvent: false });
        }
    }

    private lerArquivoComoBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const res = reader.result;
                if (typeof res === 'string') {
                    const commaIdx = res.indexOf(',');
                    const base64 = commaIdx >= 0 ? res.substring(commaIdx + 1) : res;
                    resolve(base64);
                    return;
                }
                if (res instanceof ArrayBuffer) {
                    const bytes = new Uint8Array(res);
                    let binary = '';
                    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
                    resolve(btoa(binary));
                    return;
                }
                reject(new Error('Formato de leitura nao suportado'));
            };
            reader.onerror = () => reject(reader.error || new Error('Erro ao ler arquivo'));
            reader.readAsDataURL(file);
        });
    }

    private extrairBase64(valor: string): { data: string; mime?: string } {
        const trimmed = valor.trim();
        if (!trimmed.startsWith('data:')) {
            return { data: trimmed };
        }

        const commaIdx = trimmed.indexOf(',');
        if (commaIdx < 0) {
            return { data: trimmed };
        }

        const meta = trimmed.substring(5, commaIdx);
        const mime = meta.split(';')[0] || undefined;
        const data = trimmed.substring(commaIdx + 1);
        return { data, mime };
    }

    private inferirMimeType(base64: string): string {
        const trimmed = base64.trim();
        if (trimmed.startsWith('JVBERi0')) return 'application/pdf';
        if (trimmed.startsWith('iVBORw0')) return 'image/png';
        if (trimmed.startsWith('/9j/')) return 'image/jpeg';
        return 'application/octet-stream';
    }

    private base64ToBlob(base64: string, mime: string): Blob {
        const bytes = atob(base64);
        const array = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) {
            array[i] = bytes.charCodeAt(i);
        }
        return new Blob([array], { type: mime });
    }

    mostrarCensuraPublica(): boolean {
        return this.modoEdicao() || (this.censurasPublicas()?.length ?? 0) > 0;
    }
}
